import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { leadAvatarClass, initials as leadInitials } from '../lib/leadAvatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import InlineCallRecorder from './InlineCallRecorder';
import {
  Phone, Mail, MessageSquare, Calendar, Plus,
  CheckCircle, ArrowRight, PhoneCall, Send, FileText,
  Loader2, X, Edit2, Copy, Zap,
  BellRing, XCircle, CalendarClock, CheckCircle2, Workflow,
} from 'lucide-react';
import {
  backendUrl, PRIORITY_CONFIG, STATUS_CONFIG,
  formatRelativeTime, formatDateTime, isOverdue,
} from '../utils/nurtureUtils';

// Lead Detail Panel — shared between the legacy slide-out (mode='panel') and
// the new full-page route (mode='page'). All behaviour is identical, only the
// outer wrapper differs.
const LeadDetailPanel = ({ lead, onClose, onUpdate, options, templates, authToken, mode = 'panel' }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [activities, setActivities] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [showScheduleFollowUp, setShowScheduleFollowUp] = useState(false);
  const [newFollowUp, setNewFollowUp] = useState({ scheduled_at: '', method: 'Call', notes: '' });
  const [quickNotes, setQuickNotes] = useState('');
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [templateAction, setTemplateAction] = useState(null); // 'sms' or 'email'
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [renderedTemplate, setRenderedTemplate] = useState(null);

  const headers = { Authorization: `Bearer ${authToken}` };

  useEffect(() => {
    if (lead) {
      fetchActivities();
      fetchFollowUps();
      setEditData(lead);
    }
    // eslint-disable-next-line
  }, [lead]);

  const fetchActivities = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/nurture/leads/${lead.id}/activities`, { headers });
      const data = await res.json();
      setActivities(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchFollowUps = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/nurture/follow-ups?lead_id=${lead.id}`, { headers });
      const data = await res.json();
      setFollowUps(data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleQuickAction = async (action, templateId = null) => {
    try {
      let url = `${backendUrl}/api/nurture/leads/${lead.id}/quick-action?action=${action}`;
      if (templateId) url += `&template_id=${templateId}`;
      await fetch(url, { method: 'POST', headers });
      toast.success(`${action} logged!`);
      fetchActivities();
      onUpdate();
    } catch (err) {
      toast.error('Failed to log action');
    }
  };

  const handleOpenTemplateSelector = (action) => {
    setTemplateAction(action);
    setSelectedTemplate(null);
    setRenderedTemplate(null);
    setShowTemplateSelector(true);
  };

  const handleSelectTemplate = async (template) => {
    setSelectedTemplate(template);
    try {
      const res = await fetch(`${backendUrl}/api/nurture/templates/${template.id}/render?lead_id=${lead.id}`, {
        method: 'POST', headers,
      });
      const rendered = await res.json();
      setRenderedTemplate(rendered);
    } catch (err) {
      toast.error('Failed to render template');
    }
  };

  const handleSendWithTemplate = async () => {
    if (!selectedTemplate) return;
    await handleQuickAction(templateAction, selectedTemplate.id);
    setShowTemplateSelector(false);
    setSelectedTemplate(null);
    setRenderedTemplate(null);
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleAddNote = async () => {
    if (!quickNotes.trim()) return;
    try {
      await fetch(`${backendUrl}/api/nurture/activities`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_id: lead.id, activity_type: 'note', description: quickNotes }),
      });
      toast.success('Note added!');
      setQuickNotes('');
      fetchActivities();
    } catch (err) {
      toast.error('Failed to add note');
    }
  };

  const handleSaveEdit = async () => {
    setLoading(true);
    try {
      await fetch(`${backendUrl}/api/nurture/leads/${lead.id}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(editData),
      });
      toast.success('Lead updated!');
      setEditMode(false);
      onUpdate();
    } catch (err) {
      toast.error('Failed to update lead');
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleFollowUp = async () => {
    if (!newFollowUp.scheduled_at) {
      toast.error('Please select a date and time');
      return;
    }
    try {
      await fetch(`${backendUrl}/api/nurture/follow-ups`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_id: lead.id, ...newFollowUp }),
      });
      toast.success('Follow-up scheduled!');
      setShowScheduleFollowUp(false);
      setNewFollowUp({ scheduled_at: '', method: 'Call', notes: '' });
      fetchFollowUps();
      onUpdate();
    } catch (err) {
      toast.error('Failed to schedule follow-up');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await fetch(`${backendUrl}/api/nurture/leads/${lead.id}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      toast.success('Status updated!');
      onUpdate();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  if (!lead) return null;

  const priority = PRIORITY_CONFIG[lead.priority] || PRIORITY_CONFIG.Warm;
  // STATUS_CONFIG referenced for typing/color extension (kept for parity)
  // eslint-disable-next-line no-unused-vars
  const _status = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new_lead;

  return (
    <div
      className={cn(
        (mode === 'page' || mode === 'sheet')
          ? 'w-full bg-white dark:bg-slate-900 flex flex-col rounded-xl shadow border border-slate-200 dark:border-slate-800 overflow-hidden'
          : 'fixed inset-y-0 right-0 w-full max-w-2xl bg-white dark:bg-slate-900 shadow-2xl z-50 flex flex-col'
      )}
      data-testid="lead-detail-panel"
    >
      {/* Header — full red banner only in 'page' or 'panel' mode.
          In 'sheet' mode the parent (LeadDetailSheet) renders its own header
          with the Move-to-Bank CTA, so we just emit a clean quick-actions row. */}
      {mode !== 'sheet' ? (
        <div className={cn('p-6 text-white', priority.color)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div
                className={`flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center text-white text-base font-bold shadow-md ring-2 ring-white/30 ${leadAvatarClass(lead.id || lead.name)}`}
                aria-hidden="true"
              >
                {leadInitials(lead.name)}
              </div>
              <div>
                <p className="text-sm opacity-80">{lead.lead_number}</p>
                <h2 className="text-2xl font-bold">{lead.name}</h2>
                <p className="text-sm opacity-90 mt-1">{lead.lead_type} • {lead.lead_source}</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={onClose}>
              <X className="w-6 h-6" />
            </Button>
          </div>

          {/* Quick Actions Bar */}
          <div className="flex items-center gap-2 mt-4">
            <Button size="sm" variant="secondary" onClick={() => handleQuickAction('call')}>
              <PhoneCall className="w-4 h-4 mr-1" /> Call
            </Button>
            <Button size="sm" variant="secondary" onClick={() => handleOpenTemplateSelector('sms')}>
              <MessageSquare className="w-4 h-4 mr-1" /> SMS
            </Button>
            <Button size="sm" variant="secondary" onClick={() => handleOpenTemplateSelector('email')}>
              <Mail className="w-4 h-4 mr-1" /> Email
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setShowScheduleFollowUp(true)}>
              <Calendar className="w-4 h-4 mr-1" /> Schedule
            </Button>
          </div>
        </div>
      ) : (
        /* Sheet mode — clean quick actions row (no red banner, no duplicate name) */
        <div className="px-4 sm:px-6 pt-4 pb-3 flex items-center gap-2 flex-wrap bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-800">
          <Button size="sm" variant="outline" onClick={() => handleQuickAction('call')} className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200">
            <PhoneCall className="w-4 h-4 mr-1.5" /> Call
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleOpenTemplateSelector('sms')} className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200">
            <MessageSquare className="w-4 h-4 mr-1.5" /> SMS
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleOpenTemplateSelector('email')} className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200">
            <Mail className="w-4 h-4 mr-1.5" /> Email
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowScheduleFollowUp(true)} className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200">
            <Calendar className="w-4 h-4 mr-1.5" /> Schedule
          </Button>
        </div>
      )}

      {/* Status Bar */}
      <div className="px-6 py-3 bg-slate-50 dark:bg-slate-800/60 border-b dark:border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500 dark:text-slate-400">Status:</span>
          <Select value={lead.status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-[200px] h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.statuses?.map(s => (
                <SelectItem key={s} value={s}>{options.status_labels?.[s] || s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <span>Created: {formatRelativeTime(lead.created_at)}</span>
          {lead.last_contacted_at && <span>• Last Contact: {formatRelativeTime(lead.last_contacted_at)}</span>}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
        <TabsList className="mx-6 mt-4">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="activity">Activity ({activities.length})</TabsTrigger>
          <TabsTrigger value="followups">Follow-Ups ({followUps.length})</TabsTrigger>
        </TabsList>

        <div className="flex-1 overflow-y-auto p-6">
          <TabsContent value="details" className="mt-0 space-y-4">
            {/* Contact Info */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center justify-between">
                  Contact Information
                  <Button variant="ghost" size="sm" onClick={() => setEditMode(!editMode)}>
                    <Edit2 className="w-4 h-4 mr-1" /> {editMode ? 'Cancel' : 'Edit'}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {editMode ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-slate-500">Name</label>
                        <Input value={editData.name || ''} onChange={e => setEditData({ ...editData, name: e.target.value })} />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Phone</label>
                        <Input value={editData.phone || ''} onChange={e => setEditData({ ...editData, phone: e.target.value })} />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Email</label>
                      <Input value={editData.email || ''} onChange={e => setEditData({ ...editData, email: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs text-slate-500">Source</label>
                        <Select value={editData.lead_source} onValueChange={v => setEditData({ ...editData, lead_source: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {options.lead_sources?.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Type</label>
                        <Select value={editData.lead_type} onValueChange={v => setEditData({ ...editData, lead_type: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {options.lead_types?.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Priority</label>
                        <Select value={editData.priority} onValueChange={v => setEditData({ ...editData, priority: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {options.priorities?.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button onClick={handleSaveEdit} disabled={loading} className="w-full">
                      {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Save Changes
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span>{lead.phone || 'No phone'}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <span>{lead.email || 'No email'}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 pt-3 border-t">
                      <div>
                        <p className="text-xs text-slate-500">Source</p>
                        <p className="font-medium">{lead.lead_source}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Type</p>
                        <p className="font-medium">{lead.lead_type}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Priority</p>
                        <Badge className={cn('mt-1', priority.color)}>{lead.priority}</Badge>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Notes */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 whitespace-pre-wrap">{lead.notes || 'No notes yet'}</p>
              </CardContent>
            </Card>

            {/* Quick Note */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex gap-2">
                  <Textarea
                    placeholder="Add a quick note..."
                    value={quickNotes}
                    onChange={e => setQuickNotes(e.target.value)}
                    rows={2}
                    className="flex-1"
                  />
                  <Button onClick={handleAddNote} disabled={!quickNotes.trim()}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Inline Call Recorder – auto-attaches transcript to this lead */}
            <InlineCallRecorder
              leadId={lead.id}
              leadName={lead.name}
              authToken={authToken}
              onTranscribed={() => {
                fetchActivities();
                onUpdate();
              }}
            />
          </TabsContent>

          <TabsContent value="activity" className="mt-0">
            <div className="space-y-3">
              {activities.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No activity yet</p>
              ) : (
                activities.map(activity => (
                  <div key={activity.id} className="flex gap-3 p-3 bg-slate-50 dark:bg-slate-800/60 rounded-lg">
                    <div className={cn(
                      'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                      activity.activity_type === 'call' ? 'bg-green-100 text-green-600' :
                      activity.activity_type === 'sms' ? 'bg-blue-100 text-blue-600' :
                      activity.activity_type === 'email' ? 'bg-purple-100 text-purple-600' :
                      activity.activity_type === 'status_change' ? 'bg-amber-100 text-amber-700' :
                      activity.activity_type === 'auto_scheduled' ? 'bg-emerald-100 text-emerald-700' :
                      activity.activity_type === 'reminder_due' ? 'bg-orange-100 text-orange-700' :
                      activity.activity_type === 'follow_up_cancelled' ? 'bg-red-100 text-red-600' :
                      activity.activity_type === 'follow_up_scheduled' ? 'bg-teal-100 text-teal-700' :
                      activity.activity_type === 'follow_up_completed' ? 'bg-green-100 text-green-700' :
                      activity.activity_type === 'sequence_started' ? 'bg-indigo-100 text-indigo-700' :
                      'bg-slate-200 text-slate-600'
                    )}>
                      {activity.activity_type === 'call' ? <PhoneCall className="w-4 h-4" /> :
                       activity.activity_type === 'sms' ? <MessageSquare className="w-4 h-4" /> :
                       activity.activity_type === 'email' ? <Mail className="w-4 h-4" /> :
                       activity.activity_type === 'status_change' ? <ArrowRight className="w-4 h-4" /> :
                       activity.activity_type === 'auto_scheduled' ? <Zap className="w-4 h-4" /> :
                       activity.activity_type === 'reminder_due' ? <BellRing className="w-4 h-4" /> :
                       activity.activity_type === 'follow_up_cancelled' ? <XCircle className="w-4 h-4" /> :
                       activity.activity_type === 'follow_up_scheduled' ? <CalendarClock className="w-4 h-4" /> :
                       activity.activity_type === 'follow_up_completed' ? <CheckCircle2 className="w-4 h-4" /> :
                       activity.activity_type === 'sequence_started' ? <Workflow className="w-4 h-4" /> :
                       <FileText className="w-4 h-4" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-slate-700 dark:text-slate-200">{activity.description}</p>
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">{formatRelativeTime(activity.created_at)}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="followups" className="mt-0">
            <div className="space-y-3">
              <Button onClick={() => setShowScheduleFollowUp(true)} className="w-full">
                <Plus className="w-4 h-4 mr-2" /> Schedule New Follow-Up
              </Button>

              {followUps.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No follow-ups scheduled</p>
              ) : (
                followUps.map(fu => (
                  <Card key={fu.id} className={cn(
                    'border-l-4',
                    fu.status === 'completed' ? 'border-l-green-500 bg-green-50 dark:bg-emerald-950/30' :
                    isOverdue(fu.scheduled_at) ? 'border-l-red-500 bg-red-50 dark:bg-rose-950/30' :
                    'border-l-blue-500'
                  )}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{fu.method}</p>
                          <p className={cn(
                            'text-sm',
                            fu.status === 'completed' ? 'text-green-600 dark:text-emerald-400' :
                            isOverdue(fu.scheduled_at) ? 'text-red-600 dark:text-rose-400' :
                            'text-slate-500 dark:text-slate-400'
                          )}>
                            {fu.status === 'completed' ? '✓ Completed' : formatDateTime(fu.scheduled_at)}
                          </p>
                          {fu.notes && <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">{fu.notes}</p>}
                        </div>
                        {fu.status === 'pending' && (
                          <div className="flex items-center gap-1.5">
                            <select
                              defaultValue=""
                              onChange={async (e) => {
                                const outcome = e.target.value;
                                if (!outcome) return;
                                const url = outcome === 'done'
                                  ? `${backendUrl}/api/nurture/follow-ups/${fu.id}/complete`
                                  : `${backendUrl}/api/nurture/follow-ups/${fu.id}/complete?outcome=${encodeURIComponent(outcome)}`;
                                const resp = await fetch(url, { method: 'POST', headers });
                                const data = await resp.json().catch(() => ({}));
                                if (data.auto_parked) {
                                  toast.warning(`Lead auto-parked to Long-term Nurture (3 no-contact attempts).`);
                                } else if (outcome === 'no_answer' || outcome === 'voicemail' || outcome === 'no_response') {
                                  const attempts = data.no_answer_attempts || 0;
                                  toast.info(`Attempt logged${attempts ? ` — ${attempts}/3` : ''}`);
                                } else {
                                  toast.success('Follow-up completed!');
                                }
                                e.target.value = '';
                                fetchFollowUps();
                                onUpdate();
                              }}
                              className="text-xs h-9 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2"
                              data-testid={`followup-outcome-${fu.id}`}
                              aria-label="Mark follow-up outcome"
                            >
                              <option value="">Mark…</option>
                              <option value="done">✓ Done · Spoke</option>
                              <option value="no_answer">No answer</option>
                              <option value="voicemail">Voicemail left</option>
                              <option value="no_response">No response</option>
                              <option value="scheduled">Scheduled next step</option>
                            </select>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </div>
      </Tabs>

      {/* Schedule Follow-Up Dialog */}
      <Dialog open={showScheduleFollowUp} onOpenChange={setShowScheduleFollowUp}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule Follow-Up</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Date & Time</label>
              <Input
                type="datetime-local"
                value={newFollowUp.scheduled_at}
                onChange={e => setNewFollowUp({ ...newFollowUp, scheduled_at: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Method</label>
              <Select value={newFollowUp.method} onValueChange={v => setNewFollowUp({ ...newFollowUp, method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Call">Call</SelectItem>
                  <SelectItem value="SMS">SMS</SelectItem>
                  <SelectItem value="Email">Email</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Notes</label>
              <Textarea
                value={newFollowUp.notes}
                onChange={e => setNewFollowUp({ ...newFollowUp, notes: e.target.value })}
                placeholder="e.g., Client requested call at 2pm Friday"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleFollowUp(false)}>Cancel</Button>
            <Button onClick={handleScheduleFollowUp}>Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Selector Dialog */}
      <Dialog open={showTemplateSelector} onOpenChange={setShowTemplateSelector}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {templateAction === 'sms' ? <MessageSquare className="w-5 h-5 text-blue-600" /> : <Mail className="w-5 h-5 text-purple-600" />}
              Select {templateAction === 'sms' ? 'SMS' : 'Email'} Template for {lead.name}
            </DialogTitle>
          </DialogHeader>

          <div className="flex gap-4 flex-1 overflow-hidden">
            {/* Template List */}
            <div className="w-1/3 border-r pr-4 overflow-y-auto">
              <p className="text-sm font-medium text-slate-500 mb-2">Available Templates</p>
              <div className="space-y-2">
                {templates
                  .filter(t => t.template_type === templateAction)
                  .map(template => (
                    <div
                      key={template.id}
                      onClick={() => handleSelectTemplate(template)}
                      className={cn(
                        'p-3 rounded-lg cursor-pointer border transition-all',
                        selectedTemplate?.id === template.id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10'
                          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/60'
                      )}
                    >
                      <p className="font-medium text-sm">{template.name}</p>
                      <p className="text-xs text-slate-500">{template.category}</p>
                    </div>
                  ))}
                {templates.filter(t => t.template_type === templateAction).length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">No {templateAction} templates found</p>
                )}
              </div>

              <div className="mt-4 pt-4 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    handleQuickAction(templateAction);
                    setShowTemplateSelector(false);
                  }}
                >
                  <Send className="w-4 h-4 mr-1" />
                  Log Without Template
                </Button>
              </div>
            </div>

            {/* Template Preview */}
            <div className="flex-1 overflow-y-auto">
              {selectedTemplate ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">{selectedTemplate.name}</h3>
                    <Badge>{selectedTemplate.category}</Badge>
                  </div>

                  {renderedTemplate ? (
                    <div className="space-y-3">
                      {templateAction === 'email' && renderedTemplate.rendered_subject && (
                        <div className="p-3 bg-slate-50 dark:bg-slate-800/60 rounded-lg">
                          <p className="text-xs text-slate-500 mb-1">Subject</p>
                          <p className="font-medium">{renderedTemplate.rendered_subject}</p>
                        </div>
                      )}

                      <div className="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs text-slate-500">Message Preview (personalized for {lead.name})</p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopyToClipboard(renderedTemplate.rendered_body)}
                          >
                            <Copy className="w-4 h-4 mr-1" /> Copy
                          </Button>
                        </div>
                        <pre className="whitespace-pre-wrap text-sm font-sans">{renderedTemplate.rendered_body}</pre>
                      </div>

                      <div className="p-3 bg-green-50 border border-green-200 dark:bg-emerald-950/30 dark:border-emerald-500/30 rounded-lg">
                        <p className="text-sm text-green-700 dark:text-emerald-300">
                          ✓ Variables have been replaced with {lead.name}'s actual data
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-40">
                      <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-slate-400">
                  <FileText className="w-12 h-12 mb-2" />
                  <p>Select a template to preview</p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowTemplateSelector(false)}>Cancel</Button>
            <Button
              onClick={handleSendWithTemplate}
              disabled={!selectedTemplate || !renderedTemplate}
              className={templateAction === 'sms' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-purple-600 hover:bg-purple-700'}
            >
              <Send className="w-4 h-4 mr-2" />
              Log {templateAction === 'sms' ? 'SMS' : 'Email'} Sent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadDetailPanel;
